/* eslint-disable @typescript-eslint/no-explicit-any */

import { FlightButton, FlightDropdown, FlightNav } from '@flybits/design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { capitalize, isEmpty } from 'lodash';
import { matchPath, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import FlybitsLogo from 'assets/images/flybits-logo.png';
import { LoadingIcon } from 'components/Shared/LoadingIcon/LoadingIcon';
import Notifications from 'components/Notifications/Notifications';
import { ReactComponent as RectangleSeperator } from 'assets/icons/rectangle.svg';
import Session from 'services/session';
import { getTenantId } from 'helpers/templated-experience.helper';
import AuthAPI from 'services/api/auth.api';
import SettingAPI from 'services/api/setting.api';
// TODO: Replace end of support package for theme switching
import ThemeSwitcher from 'react-css-vars';
import UseAsyncState from 'hooks/useAsyncState';
import { FBTokenClaims } from 'types/common';
import { useDispatch } from 'react-redux';

interface AppFlightNavProps {
  user: any;
  flags?: Record<string, boolean>;
  children: React.ReactNode;
}

function AppFlightNav({ user, flags, children }: AppFlightNavProps) {
  const session = useMemo(() => new Session(getTenantId()), []);
  const authAPI = useMemo(() => new AuthAPI(), []);
  const settingsApi = useMemo(() => new SettingAPI(), []);
  const dispatch = useDispatch();
  const [project, setProject] = useState<{ Name?: string }>({});
  const [isSigningOut, setIsSigningOut] = useState(false);

  const { pathname } = useLocation();
  const match = matchPath<{ projectId: string }>(pathname, {
    path: '/project/:projectId',
  });
  const projectId = match?.params?.projectId;

  const [themeData, isThemeDataLoading] = UseAsyncState(
    () => settingsApi.getThemeSettings().then((r) => r.settings),
    [],
  );

  let logoUrl;
  let appName;
  let appNameTextColor: string;
  let navbarBgColor = '#ffffff';

  if (themeData && !isThemeDataLoading) {
    const _themeData = JSON.parse(themeData?.themeData) || '';
    if (!isEmpty(_themeData.logoUrl)) {
      logoUrl = _themeData.logoUrl;
    }
    if (!isEmpty(_themeData.appName)) {
      appName = _themeData.appName;
    }
    if (!isEmpty(_themeData.appNameTextColor)) {
      appNameTextColor = _themeData.appNameTextColor;
    }
    if (!isEmpty(_themeData.navbarBgColor)) {
      navbarBgColor = _themeData.navbarBgColor;
    }
  }

  const [theme, setTheme] = useState({
    navbarBgColor: '',
    appNameTextColor: '',
  });

  useEffect(() => {
    if (!projectId) return;
    setTheme({
      navbarBgColor: navbarBgColor,
      appNameTextColor: appNameTextColor,
    });
    //eslint-disable-next-line
  }, [isThemeDataLoading, projectId]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await authAPI.signOut();
    } finally {
      try {
        await session.unauthorizeUser();
        window.location.replace(`/signed-out/${projectId}`);
      } catch (error) {
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: { content: `Could not sign out. Please try again.`, type: 'error' },
        });
        setIsSigningOut(false);
      }
    }
  };

  const devPortalRedirect = async () => {
    const token = await session.getToken();
    const { authMethod } = jwtDecode<FBTokenClaims>(token);
    if (authMethod === 'samllogin') {
      window.open(`${process.env.REACT_APP_DEV_PORTAL_URL}/external-auth-redirect/${projectId}?token=${token}`);
    } else {
      window.open(`${process.env.REACT_APP_DEV_PORTAL_URL}/project/${projectId}`);
    }
  };

  function getSidebarRoutes() {
    if (!projectId) {
      return [];
    }

    const routes = [
      {
        label: 'Experiences',
        icon: 'workflow',
        location: `/project/${projectId}`,
        key: 'sidebar-nav-experiences',
      },
      {
        label: 'Locations',
        icon: 'locationMap',
        location: `/project/${projectId}/location-management`,
        key: 'sidebar-nav-location-management',
      },
      ...(flags && flags['tx_template_library_modal']
        ? [
            {
              label: 'Context files',
              icon: 'contextFiles',
              location: `/project/${projectId}/context-files`,
              key: 'sidebar-nav-context-files',
            },
          ]
        : []),
      {
        label: 'Analytics',
        icon: 'report',
        location: `/project/${projectId}/analytics`,
        key: 'sidebar-nav-analytics',
      },
      {
        label: 'Content Manager',
        icon: 'gallery',
        location: `/project/${projectId}/content-manager`,
        key: 'sidebar-nav-content-manager',
      },
    ];
    if (user && user.level >= 20) {
      routes.splice(1, 0, {
        label: 'Audit History',
        icon: 'customerInsight',
        location: `/project/${projectId}/settings/audit-history`,
        key: 'sidebar-nav-audit-history',
      });
    }
    routes.push({
      label: 'Zones and Modules',
      icon: 'collection',
      location: `/project/${projectId}/zones`,
      key: 'sidebar-nav-zones',
    });

    if (flags && flags['tx_merchant_portal']) {
      routes.push({
        label: 'Merchant Portal',
        icon: 'merchant',
        location: `/project/${projectId}/merchants`,
        key: 'sidebar-nav-merchant-portal',
      });
    }

    return routes;
  }

  useEffect(() => {
    if (!projectId) return;
    settingsApi.getProjectSettings(projectId).then((project) => {
      setProject(project);
    });
  }, [projectId, settingsApi]);

  return (
    <ThemeSwitcher theme={theme}>
      {isSigningOut && (
        <div className="app__logout-overlay" aria-label="signing out">
          <LoadingIcon className="app__logout-overlay__icon" />
        </div>
      )}
      <FlightNav
        user={user}
        project={{
          id: 'id',
          name: `${appName || process.env.REACT_APP_NAME}`,
          environment: `${project.Name ? project?.Name + ' | ' : ''}${capitalize(process.env.REACT_APP_ENV)}`,
          image: <img alt="logo" src={logoUrl || FlybitsLogo} />,
        }}
        hasSidebar={true}
        collapsed={true}
        routes={getSidebarRoutes()}
        isActiveRouteExactMatch={true}
        actionables={
          <>
            <FlightDropdown
              className="flight-nav-apps"
              trigger={
                <FlightButton
                  iconLeft="circleApps"
                  onClick={() => undefined}
                  isPropagateUpperActions
                  theme="minor"
                  label=""
                  ariaLabel=" "
                />
              }
            >
              <>
                <FlightButton
                  className="redirect-project-settings"
                  onClick={devPortalRedirect}
                  iconRight="openInNew"
                  label="Project Settings"
                  size="small"
                  theme="minor"
                />
              </>
            </FlightDropdown>
            {flags && flags['tx_ao_notifications'] && (
              <div>
                <Notifications />
                <RectangleSeperator />
              </div>
            )}
          </>
        }
        handleSignOut={handleSignOut}
      >
        {children}
      </FlightNav>
    </ThemeSwitcher>
  );
}

export default AppFlightNav;
